<template>
  <div class="ep-wrap" ref="wrapEl">
    <button class="ep-trigger" :class="{ 'ep-trigger--open': open }" @click.stop="toggle" type="button" title="Emoji">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
        <line x1="9" y1="9" x2="9.01" y2="9"/>
        <line x1="15" y1="9" x2="15.01" y2="9"/>
      </svg>
    </button>
    <Teleport to="body">
      <div v-if="open" class="ep-popup" :style="popupStyle" @click.stop>
        <div class="ep-tabs">
          <button
            v-for="cat in CATS"
            :key="cat.id"
            class="ep-tab"
            :class="{ 'ep-tab--active': activeTab === cat.id }"
            @click="activeTab = cat.id"
            :title="cat.label"
          >{{ cat.icon }}</button>
        </div>
        <div class="ep-grid">
          <button
            v-for="em in currentEmojis"
            :key="em"
            class="ep-em"
            @click="pick(em)"
          >{{ em }}</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const emit = defineEmits<{ (e: 'pick', emoji: string): void }>()

const open = ref(false)
const wrapEl = ref<HTMLElement | null>(null)
const popupStyle = ref<Record<string, string>>({})

const CATS = [
  { id: 'smileys', icon: '😊', label: 'Rostos' },
  { id: 'hands',   icon: '👍', label: 'Gestos' },
  { id: 'hearts',  icon: '❤️', label: 'Corações' },
  { id: 'objects', icon: '📋', label: 'Objetos' },
  { id: 'symbols', icon: '✅', label: 'Símbolos' },
]

const EMOJIS: Record<string, string[]> = {
  smileys: [
    '😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃',
    '😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙',
    '😛','😝','😜','🤪','😎','🥳','😏','😒','😔','😟',
    '🙁','😣','😖','😫','😩','🥺','😢','😭','😤','😠',
    '😡','🤯','😳','🥵','🥶','😱','😨','😰','😓','🤗',
    '🤔','🤭','🫡','😶','😐','😑','😬','🙄','😯','😲',
    '🥱','😴','😷','🤒','🤕','🤢','🤧','🫠','😵','🤐',
  ],
  hands: [
    '👍','👎','👌','🤌','🤙','✌️','🤞','🫰','🤟','🤘',
    '👏','🙌','🤝','🤜','🤛','👊','✊','🫶','🙏','🤲',
    '👐','🫴','🫳','🤚','✋','🖐️','👋','🫱','🫲','💪',
    '🦵','🦶','👂','🦻','👃','🫦','👁️','👀','🫀','🧠',
  ],
  hearts: [
    '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔',
    '❤️‍🔥','❤️‍🩹','💕','💞','💓','💗','💖','💝','💘','💟',
    '♥️','🫀','💌','💋','💏','💑','👫','👬','👭','🥂',
  ],
  objects: [
    '📱','💻','🖥️','⌨️','🖨️','📧','📞','☎️','📟','📠',
    '📝','📋','📄','📁','🗂️','📅','📆','⏰','⌚','⏱️',
    '🔔','🔕','💡','🔦','🕯️','🪔','🔍','🔎','🔑','🗝️',
    '🔒','🔓','🔨','⚙️','🛠️','💰','💳','📊','📈','📉',
    '🏷️','🎁','🎉','🎊','🎈','🎀','🏆','🥇','⭐','🌟',
  ],
  symbols: [
    '✅','❌','⚠️','🚫','⛔','❓','❗','‼️','⁉️','🔴',
    '🟠','🟡','🟢','🔵','🟣','⚫','⚪','🔺','🔻','▶️',
    '⏩','⏪','⏫','⏬','🔄','🔃','↩️','↪️','🔙','🔚',
    '🔛','🔜','🔝','➡️','⬅️','⬆️','⬇️','↕️','↔️','🔀',
    '💯','🆗','🆕','🆙','🆒','🆓','🆖','🆘','🔞','📵',
    '🕐','🕑','🕒','🕓','🕔','🕕','🕖','🕗','🕘','🕙',
  ],
}

const activeTab = ref('smileys')
const currentEmojis = computed(() => EMOJIS[activeTab.value] ?? [])

function toggle() {
  if (!open.value) {
    open.value = true
    calcPosition()
  } else {
    open.value = false
  }
}

function calcPosition() {
  if (!wrapEl.value) return
  const rect = wrapEl.value.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const popupH = 340
  const popupW = 360

  const top = spaceBelow >= popupH
    ? rect.bottom + 4
    : rect.top - popupH - 4
  const left = Math.min(rect.left, window.innerWidth - popupW - 8)

  popupStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${Math.max(8, left)}px`,
    width: `${popupW}px`,
    zIndex: '99999',
  }
}

function pick(em: string) {
  emit('pick', em)
  open.value = false
}

function onOutsideClick() {
  open.value = false
}

onMounted(() => document.addEventListener('click', onOutsideClick))
onBeforeUnmount(() => document.removeEventListener('click', onOutsideClick))
</script>

<style scoped>
.ep-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.ep-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #888;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  transition: color 0.15s, background 0.15s;
}
.ep-trigger:hover,
.ep-trigger--open {
  color: #c0392b;
  background: rgba(192, 57, 43, 0.08);
}
</style>

<style>
.ep-popup {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.ep-tabs {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  padding: 4px 4px 0;
  gap: 2px;
  background: #fafafa;
}

.ep-tab {
  flex: 1;
  border: none;
  background: transparent;
  padding: 5px 2px;
  font-size: 16px;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  border-bottom: 2px solid transparent;
  transition: background 0.12s;
}
.ep-tab:hover { background: #f0f0f0; }
.ep-tab--active {
  background: #fff;
  border-bottom-color: #c0392b;
}

.ep-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 1px;
  padding: 6px;
  overflow-y: auto;
  max-height: 280px;
}

.ep-em {
  border: none;
  background: transparent;
  font-size: 18px;
  line-height: 1;
  padding: 4px;
  cursor: pointer;
  border-radius: 5px;
  transition: background 0.1s, transform 0.1s;
  text-align: center;
}
.ep-em:hover {
  background: #f5f5f5;
  transform: scale(1.2);
}
</style>
